
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useAgentConfig } from '../contexts/AgentConfigContext';
import SettingsModal from '../settings/SettingsModal';
import { Settings } from 'lucide-react';
import config from '../config.js';

const NavBar = (props) => {
  const { isDarkMode } = useDarkMode();
  const { securityParams } = useAgentConfig();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [imageError, setImageError] = useState(false);
  const userMenuRef = useRef(null);

  const paramCount = Object.keys(securityParams || {}).length;

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Reset image error when user changes
  useEffect(() => {
    setImageError(false);
  }, [props.user?.photoURL]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2050, display: 'flex', pointerEvents: 'none' }}>
      {/* Sidebar spacer - matches sidebar width */}
      <div
        style={{
          width: props.isSideBar ? '260px' : '56px',
          minWidth: props.isSideBar ? '260px' : '56px',
          flexShrink: 0,
          transition: 'width 0.3s ease'
        }}
      />

      {/* Main navbar */}
      <nav
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: '56px',
          backgroundColor: 'var(--bg-surface)',
          boxShadow: 'none',
          transition: 'background-color 0.2s ease',
          pointerEvents: 'auto'
        }}
      >
         {/* Left side - Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {config.brandLogoUrl && (
            <img
              src={config.brandLogoUrl}
              /* Decorative: the brandName span below is the accessible name, and an alt
                 repeating it makes a screen reader announce the brand twice. */
              alt=""
              style={{
                height: `${32 * config.brandLogoScale}px`,
                width: 'auto',
                maxWidth: '200px',
                objectFit: 'contain',
                filter: isDarkMode ? 'invert(1) brightness(1.2)' : 'none',
                transition: 'filter 0.2s ease'
              }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span style={{
              fontSize: '18px',
              fontWeight: 700,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '-0.02em',
              color: 'var(--fg-primary)',
              transition: 'color 0.2s ease',
              lineHeight: '1'
            }}>
              {config.brandName}
            </span>
            <span style={{
              fontSize: '17px',
              fontWeight: 500,
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              letterSpacing: '0.02em',
              color: 'var(--fg-primary)',
              transition: 'color 0.2s ease',
              lineHeight: '1',
              paddingLeft: '16px'
            }}>
              {config.brandTagline}
            </span>
          </div>
        </div>

        {/* Right side - Settings and User Profile */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Theme choice and security parameters both live in Settings now —
              two dropdowns competing for the same corner became one door. The
              count stays on the button so configured parameters remain visible
              without opening it. */}
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold text-fg-secondary bg-surface-raised hover:text-brand"
            style={{ border: 'none', cursor: 'pointer', transition: 'color var(--d-base) var(--ease-out)' }}
            aria-label="Open settings"
          >
            <Settings size={16} aria-hidden="true" />
            <span>Settings</span>
            {paramCount > 0 && (
              <span
                className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold bg-success-soft text-success"
                title={`${paramCount} security parameter${paramCount !== 1 ? 's' : ''} in use`}
              >
                {paramCount}
              </span>
            )}
          </button>

          {/* User Profile with Dropdown */}
          <div style={{ position: 'relative' }} ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                paddingLeft: '8px',
                paddingRight: '12px',
                paddingTop: '6px',
                paddingBottom: '6px',
                borderRadius: '20px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                color: 'var(--fg-primary)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-raised)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* User Avatar */}
              {props.user?.photoURL && !imageError ? (
                <img
                  src={props.user.photoURL}
                  alt={props.user?.name || 'User'}
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  className="w-8 h-8 rounded-full object-cover block border-2 border-line"
                  onError={(e) => {
                    console.error('Image failed to load:', props.user.photoURL);
                    setImageError(true);
                  }}
                  onLoad={() => {
                  }}
                />
              ) : (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-brand-fg shrink-0 bg-brand"
                >
                  {getUserInitials(props.user?.name)}
                </div>
              )}

              {/* User Name */}
              <span className="text-sm font-medium">
                {props.user?.name}
              </span>
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  marginTop: '8px',
                  minWidth: '240px',
                  borderRadius: '12px',
                  boxShadow: 'var(--shadow-float)',
                  border: '1px solid var(--line)',
                  backgroundColor: 'var(--bg-surface)',
                  overflow: 'hidden',
                  zIndex: 1000
                }}
              >
                {/* User Info Section */}
                <div style={{
                  padding: '16px',
                  borderBottom: '1px solid var(--line)'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--fg-primary)',
                    marginBottom: '4px'
                  }}>
                    {props.user?.name}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--fg-muted)'
                  }}>
                    {props.user?.email}
                  </div>
                </div>

                {/* Shared Sessions — the one place to see every live link and
                    turn any of them off. Lives here because it is about the
                    USER, not about one session (that stays on the row). */}
                {props.onOpenSharedLinks && (
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      props.onOpenSharedLinks();
                    }}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: 500,
                      border: 'none',
                      borderBottom: '1px solid var(--line)',
                      background: 'transparent',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'var(--fg-primary)',
                      transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-raised)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    Shared Sessions
                  </button>
                )}

                {/* Logout Button */}
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    props.handleLogout();
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: 500,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'var(--danger)',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--danger-soft)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Portals to document.body, escaping this shell's pointer-events:none
          and its z-2050 stacking context. */}
      <SettingsModal open={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
};

NavBar.propTypes = {
  scrollDirection: PropTypes.number.isRequired,
  isSideBar: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    name: PropTypes.string.isRequired,
    email: PropTypes.string,
    photoURL: PropTypes.string,
  }),
  handleLogout: PropTypes.func.isRequired,
  onOpenSharedLinks: PropTypes.func,
};

export default NavBar;
